import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Download, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DetailedStockSheet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/stock/detailed-stock-sheet`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load detailed stock sheet');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter(r =>
      r.part_id.toLowerCase().includes(q) ||
      (r.part_name || '').toLowerCase().includes(q) ||
      (r.steel_grade || '').toLowerCase().includes(q) ||
      (r.section || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const exportCsv = () => {
    if (!data) return;
    const stages = data.stages;
    const vendors = data.vendors;
    const headerTop = ['', '', 'STAGE', ...stages.slice(1).map(() => ''), 'VENDOR WIP', ...vendors.slice(1).map(() => '')];
    const headerSub = ['S. No', 'Part ID', ...stages, ...vendors];
    const lines = [headerTop.join(','), headerSub.join(',')];
    filteredRows.forEach(r => {
      const cells = [
        r.s_no, `"${r.part_id}"`,
        ...stages.map(s => r.stage_qty[s] || 0),
        ...vendors.map(v => r.vendor_qty[v] || 0),
      ];
      lines.push(cells.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `detailed-stock-sheet-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/export/detailed-stock-sheet-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `stock-summary-${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Failed to export PDF'); }
  };

  if (loading) return <div className="p-12 text-center text-text-muted">Loading detailed stock sheet…</div>;
  if (!data) return <div className="p-12 text-center text-red-600">Failed to load.</div>;

  const stages = data.stages;
  const vendors = data.vendors;
  const totals = data.totals || { stage: {}, vendor: {}, grand_total: 0 };
  const hasVendors = vendors.length > 0;

  return (
    <div className="space-y-6" data-testid="detailed-stock-sheet-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="detailed-stock-title">STOCK SUMMARY</h1>
          <p className="text-sm text-text-muted mt-2">Quantity per stage (internal) and per vendor WIP, in pieces. Empty parts are hidden.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search part / grade / section..." className="pl-8 rounded-none w-72" data-testid="detailed-stock-search" />
          </div>
          <Button onClick={exportCsv} variant="outline" className="rounded-sm" data-testid="detailed-stock-export">
            <Download className="w-4 h-4 mr-2" />EXPORT CSV
          </Button>
          <Button onClick={exportPdf} className="rounded-sm bg-slate-900 text-white hover:bg-slate-800" data-testid="detailed-stock-export-pdf">
            <Download className="w-4 h-4 mr-2" />EXPORT PDF
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border shadow-sm overflow-auto" data-testid="detailed-stock-table-wrapper">
        <table className="text-xs border-collapse" style={{ tableLayout: 'fixed', minWidth: '100%' }}>
          <colgroup>
            <col style={{ width: '48px' }} />
            <col style={{ width: '130px' }} />
            {stages.map(s => <col key={`cg-s-${s}`} style={{ width: '110px' }} />)}
            {vendors.map(v => <col key={`cg-v-${v}`} style={{ width: '110px' }} />)}
            <col style={{ width: '90px' }} />
          </colgroup>
          <thead>
            <tr className="bg-slate-900 text-white">
              <th rowSpan={2} className="px-2 py-2 border border-slate-700 text-left sticky left-0 bg-slate-900 z-10">S. No</th>
              <th rowSpan={2} className="px-2 py-2 border border-slate-700 text-left sticky left-12 bg-slate-900 z-10">Part ID</th>
              <th colSpan={stages.length} className="px-2 py-2 border border-slate-700 text-center bg-blue-700">STAGE</th>
              {hasVendors && (
                <th colSpan={vendors.length} className="px-2 py-2 border border-slate-700 text-center bg-amber-700">VENDOR WIP</th>
              )}
              <th rowSpan={2} className="px-2 py-2 border border-slate-700 text-right">Total</th>
            </tr>
            <tr className="bg-slate-800 text-white" style={{ height: '84px' }}>
              {stages.map(s => (
                <th key={`s-${s}`} className="px-1 py-2 border border-slate-700 text-center font-semibold align-middle" data-testid={`header-stage-${s}`} style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.15' }}>{s}</th>
              ))}
              {vendors.map(v => (
                <th key={`v-${v}`} className="px-1 py-2 border border-slate-700 text-center font-semibold align-middle" data-testid={`header-vendor-${v}`} style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.15' }}>{v}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={2 + stages.length + vendors.length + 1} className="px-4 py-12 text-center text-text-muted">No parts found</td></tr>
            ) : filteredRows.map(r => (
              <tr key={r.part_id} className="border-b border-gray-200 hover:bg-blue-50/40" data-testid={`detailed-row-${r.part_id}`}>
                <td className="px-2 py-1.5 border border-gray-200 font-mono sticky left-0 bg-white text-center">{r.s_no}</td>
                <td className="px-2 py-1.5 border border-gray-200 font-mono font-semibold sticky left-12 bg-white" title={r.part_name}>{r.part_id}</td>
                {stages.map(s => {
                  const q = r.stage_qty[s] || 0;
                  return (
                    <td key={`${r.part_id}-s-${s}`} className={`px-2 py-1.5 border border-gray-200 text-right font-mono ${q > 0 ? 'text-blue-700 font-semibold' : 'text-gray-300'}`} data-testid={`cell-${r.part_id}-stage-${s}`}>
                      {q > 0 ? q : ''}
                    </td>
                  );
                })}
                {vendors.map(v => {
                  const q = r.vendor_qty[v] || 0;
                  return (
                    <td key={`${r.part_id}-v-${v}`} className={`px-2 py-1.5 border border-gray-200 text-right font-mono ${q > 0 ? 'text-amber-700 font-semibold' : 'text-gray-300'}`} data-testid={`cell-${r.part_id}-vendor-${v}`}>
                      {q > 0 ? q : ''}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 border border-gray-200 text-right font-mono font-bold text-slate-900">{r.total}</td>
              </tr>
            ))}
          </tbody>
          {filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-100 font-semibold">
                <td colSpan={2} className="px-2 py-2 border border-slate-300 text-right uppercase tracking-wider text-[10px]">Totals</td>
                {stages.map(s => (
                  <td key={`t-s-${s}`} className="px-2 py-2 border border-slate-300 text-right font-mono text-blue-700" data-testid={`total-stage-${s}`}>{totals.stage[s] || 0}</td>
                ))}
                {vendors.map(v => (
                  <td key={`t-v-${v}`} className="px-2 py-2 border border-slate-300 text-right font-mono text-amber-700" data-testid={`total-vendor-${v}`}>{totals.vendor[v] || 0}</td>
                ))}
                <td className="px-2 py-2 border border-slate-300 text-right font-mono font-bold text-slate-900" data-testid="grand-total">{totals.grand_total || 0}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
